import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
  Divider,
} from '@mui/material';
import { Users, BarChart2, Settings, LayoutDashboard } from 'lucide-react'; //LandPlot
import LogoutButton from '../LogoutButton';
import { useAuth } from '../../contexts/AuthContext';
import { SuperAdminContentType } from '../../types/superAdmin.types';
import { ListSharp } from '@mui/icons-material';

interface SidebarProps {
  activeContent: SuperAdminContentType;
  onContentChange: (content: SuperAdminContentType) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  activeContent,
  onContentChange,
}) => {
  const theme = useTheme();
  const { authStatus } = useAuth();
  const userName = authStatus.metadata?.firstName || 'Super Admin';

  const menuItems = [
    { id: 'users' as const, label: 'User Management', icon: Users },
    { id: 'category' as const, label: 'Category Management', icon: ListSharp },
    {
      id: 'regions' as const,
      label: 'Region Management',
      icon: LayoutDashboard,
    },
    { id: 'analytics' as const, label: 'Analytics', icon: BarChart2 },
    { id: 'settings' as const, label: 'Settings', icon: Settings },
  ];

  return (
    <Box
      sx={{
        width: 280,
        backgroundColor: 'background.paper',
        borderRight: `1px solid ${theme.palette.divider}`,
        borderColor: 'divider',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="primary" fontWeight="bold">
          Super Admin
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ p: 3 }}>
        <Typography variant="subtitle1" color="text.secondary">
          Welcome back,
        </Typography>
        <Typography variant="h6" color="text.primary" fontWeight="bold">
          {userName}
        </Typography>
      </Box>

      <List sx={{ px: 2, flexGrow: 1 }}>
        {menuItems.map(({ id, label, icon: Icon }) => (
          <ListItem key={id} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => onContentChange(id)}
              selected={activeContent === id}
              sx={{
                borderRadius: '12px',
                '&.Mui-selected': {
                  backgroundColor: 'primary.lighter',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
              }}
            >
              <Icon size={20} />

              <ListItemText primary={label} sx={{ ml: 2 }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box sx={{ p: 2, mt: 'auto' }}>
        <LogoutButton variant="outlined" color="primary" />
      </Box>
    </Box>
  );
};

export default Sidebar;
